import { AsyncPipe, NgIf } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectInfrastructureService } from '@app/shared/services/project-infrastructure.service';
import { AuthService } from '@auth0/auth0-angular';
import { PastMeetingSummary, User } from 'lfx-pcc';
import { catchError, filter, map, NEVER, Observable, of, switchMap, take } from 'rxjs';

import { getV2UiBaseUrl, LF_MASTER_LOGO_URL, V2_UI_REDIRECT_PROJECTS } from '@app/shared/constants';
import { MeetingSummaryFormComponent } from './components/meeting-summary-form/meeting-summary-form.component';
import { MeetingSummaryHeaderComponent } from './components/meeting-summary-header/meeting-summary-header.component';

@Component({
  selector: 'lfx-meeting-ai-summary',
  standalone: true,
  imports: [NgIf, MeetingSummaryHeaderComponent, MeetingSummaryFormComponent, AsyncPipe],
  templateUrl: './meeting-ai-summary.component.html',
  styleUrls: ['./meeting-ai-summary.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MeetingAiSummaryComponent implements OnInit {
  public meeting$: Observable<PastMeetingSummary | null>;
  public user$: Observable<User | null | undefined>;
  public form: FormGroup;
  public logoURL: string = LF_MASTER_LOGO_URL;
  public password: string = '';

  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private pisService: ProjectInfrastructureService = inject(ProjectInfrastructureService);
  private router: Router = inject(Router);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private authService: AuthService = inject(AuthService);

  public ngOnInit(): void {
    this.form = this.formBuilder.group({
      summary: [null],
      nextSteps: [null],
      recap: [null],
      approved: [false],
      requires_approval: [false],
    });
    this.password = this.activatedRoute.snapshot.queryParams.password || '';

    this.getPastMeetingSummaries();
  }

  public refresh(): void {
    this.getPastMeetingSummaries();
  }

  private handleError(error: any) {
    console.info('Error', error.message);
    console.info('Error status', error.status);
    if (error.status > 499) {
      this.router.navigate(['/service-unavailable']);
    } else if (error.status === 401 || error.status === 403) {
      this.router.navigate(['/not-authorized']);
    } else if (error.status === 404) {
      this.router.navigate(['/not-found']);
    } else {
      this.router.navigate(['.'], {
        queryParams: { password: this.password, error: 'auth0' },
        relativeTo: this.activatedRoute,
      });
    }
    return of(null);
  }

  private formatSummaryContent(summary: { label: string; summary: string }[]): string {
    if (!summary || summary.length === 0) {
      return '';
    }

    if (summary.length === 1) {
      return `<p>${summary[0].summary}</p>`;
    }

    let formattedContent = '<div class="summary-list">';

    // Add Quick Recap section
    if (summary && summary.length > 0) {
      summary.forEach((detail) => {
        if (detail.summary) {
          formattedContent += `<div class="flex flex-col gap-2">
          <strong>${detail.label}:</strong>
          <span>${detail.summary}</span>
          </div><br>`;
        }
      });
    }

    formattedContent += '</div>';

    return formattedContent;
  }

  private formatNextStepsContent(nextSteps: string[]): string {
    if (!nextSteps || nextSteps.length === 0) {
      return '';
    }

    if (nextSteps.length === 1) {
      return `<p>${nextSteps[0]}</p>`;
    }

    let formattedContent = '<ul>';

    // Add Next Steps section
    if (nextSteps) {
      nextSteps.forEach((step) => {
        formattedContent += `<li>${step}</li>`;
      });
    }

    formattedContent += '</ul>';

    return formattedContent;
  }

  private getPastMeetingSummaries() {
    const query = new HttpParams({ fromObject: { password: this.password } });
    this.meeting$ = this.authService.isLoading$.pipe(
      filter((isLoading) => !isLoading),
      take(1),
      // Warm the Auth0 token cache so AuthHttpInterceptor attaches the token below; anonymous users fall back to null.
      switchMap(() => this.authService.getAccessTokenSilently().pipe(catchError(() => of(null)))),
      switchMap(() => this.pisService.getPastMeetingSummaries(this.activatedRoute.snapshot.params.id, query)),
      catchError((error) => {
        return this.handleError(error);
      }),
      switchMap((meeting) => {
        // Redirect approved summaries for specific project slugs to the LFX V2 UI meeting page (without password).
        // Unapproved summaries stay in the v1 experience so reviewers can still edit and approve them here.
        // Return NEVER so the AsyncPipe stays in the loading state while the browser navigates away —
        // prevents a flash of the old summary UI before redirect.
        const projectSlug = meeting?.[0]?.project_slug;
        const meetingId = meeting?.[0]?.meeting_id ?? this.activatedRoute.snapshot.params.id;
        const isApproved = meeting?.[0]?.approved;
        if (isApproved && projectSlug && V2_UI_REDIRECT_PROJECTS.includes(projectSlug)) {
          window.location.replace(`${getV2UiBaseUrl()}/meetings/${meetingId}`);
          return NEVER;
        }
        return of(meeting);
      }),
      map((meeting) => {
        if (meeting?.[0]) {
          const data = meeting[0];

          this.form.patchValue({
            recap: data.edited_summary_overview ? data.edited_summary_overview : data.summary_overview,
            summary: this.formatSummaryContent(data.edited_summary_details?.length > 0 ? data.edited_summary_details : data.summary_details),
            nextSteps: this.formatNextStepsContent(data.edited_next_steps ? data.edited_next_steps : data.next_steps),
            approved: data.approved,
            requires_approval: data.requires_approval,
          });
        }

        return meeting ? meeting[0] : null;
      })
    );
  }
}
